import { RNG } from '../core/rng'
import { mapSeed, resolveTerrain, TerrainConfig } from '../core/terrain'
import { gemRadius } from './footprint'
import {
  baseStats,
  Enemy,
  FloatingText,
  Particle,
  Pickup,
  PickupKind,
  Projectile,
  Stats,
  WeaponState,
  ItemState,
  Rarity,
} from './types'

export interface Player {
  x: number
  z: number
  vx: number
  vz: number
  y: number
  vy: number
  facing: number
  moveDir: { x: number; z: number }
  radius: number
  hp: number
  level: number
  xp: number
  xpToNext: number
  invuln: number // seconds of i-frames remaining
  dashCd: number
  dashTime: number // remaining slide duration (speed boost + i-frames)
  jumpCd: number // cooldown for the jump
  jumpBuf: number // input buffer: a jump pressed just before it's legal fires the instant it becomes legal (not dropped)
  slideBuf: number // input buffer for slide/dash (same idea — snappy controls)
  alive: boolean
  flash: number
  // status
  shield: number
  regenAccum: number
}

export type RunPhase = 'playing' | 'levelup' | 'dead' | 'won' | 'paused' | 'shop' | 'chest' | 'microwave'

export interface MapConfig {
  id: string
  name: string
  groundColor: number
  fogColor: number
  accentColor: number
  radius: number // arena radius
  surviveSeconds: number // anti-stall deadline: the portal self-activates if the player dawdles past this
  /** Seconds until the boss portal OPENS — the real run arc (start weak → power spike → boss). Falls back to the
   *  old 45s cap if unset. surviveSeconds stays the much-later anti-stall fallback. */
  goalSeconds?: number
  /** Themed enemy ids this biome spawns. Empty/undefined = global pool. */
  enemyPool?: string[]
  /** Themed boss ids (in order of appearance). Undefined = global BOSS_LIST. */
  bossPool?: string[]
  /** Terrain relief amplitude (world units). 0/undefined = flat. Shared by sim + render. */
  elevation?: number
  /** Per-biome terrain personality (octave mix). Undefined = default character. */
  terrain?: Partial<TerrainConfig>
}

export type ShrineType = 'magnet' | 'gold' | 'heal' | 'treasure' | 'battle' | 'curse' | 'charge' | 'moai' | 'microwave'

export interface Shrine {
  id: number
  x: number
  z: number
  type: ShrineType
  used: boolean
  charge?: number // 'charge' shrine: 0..1 fill while the player stands in it (Megabonk-style channel for a buff)
}

/** A telegraphed boss attack: a warning decal that resolves into damage on expiry. */
export interface Telegraph {
  id: number
  x: number
  z: number
  radius: number
  timer: number // counts down; damage applies at 0
  maxTimer: number
  damage: number
  owner?: string // boss defId that cast it (cause-of-death attribution)
}

/** CF2 — a brief visual ring marking an explosion's actual lethal footprint (render-only). */
export interface Blast {
  x: number
  z: number
  radius: number
  t: number // age (s), 0..maxT
  maxT: number
  color: number
}

export type StageObjective = 'survive' | 'portal' | 'finalboss' | 'won' | 'endless'
/** MP6 — the pre-portal GOAL of a stage (what opens the boss portal). 'survive' = the classic timer. */
export type StageGoal = 'survive' | 'elite-hunt' | 'slay'

/** Boss teleporter: walk in and channel it to summon the stage boss. */
export interface Portal {
  x: number
  z: number
  channel: number // 0..1 activation progress
  active: boolean // summoned the boss
}

/** A short-lived death "pop": the dead enemy's sprite launches up and shrinks away. */
export interface Corpse {
  x: number
  z: number
  y: number
  vy: number
  vx: number // horizontal "bonk" launch in the hit direction (from the killing blow's knockback)
  vz: number
  defId: string
  color: number
  size: number
  life: number
  maxLife: number
}

/** Static cosmetic scenery scattered around the arena per biome. */
export interface Prop {
  x: number
  z: number
  sprite: string
  scale: number
}

/** A solid obstacle that blocks player + enemy movement (procedural per run). */
export interface Obstacle {
  x: number
  z: number
  radius: number
  sprite: string
  scale: number
  blocksShots?: boolean // solid cover — stops physical projectiles
}

/** A destructible object (alien pod / crate / barrel) the player smashes for random loot. */
export interface Breakable {
  id: number
  x: number
  z: number
  hp: number
  maxHp: number
  radius: number
  sprite: string
  scale: number
  flash: number
}

let nextId = 1
export function allocId(): number {
  return nextId++
}

/** XP-gem tier breakpoints (small/medium/large) by XP value — also used by the on-map merge pass. */
export const XP_TIER_T2 = 5
export const XP_TIER_T3 = 18
export function xpGemTier(value: number): number {
  if (value >= XP_TIER_T3) return 3
  if (value >= XP_TIER_T2) return 2
  return 1
}

/**
 * The full mutable state of one run. Systems read/write this. No rendering or
 * DOM references live here.
 */
export class World {
  rng: RNG
  seed = 0 // MP8: the numeric seed this run was created from (for display + share/replay)
  player: Player
  stats: Stats
  weapons: WeaponState[] = []
  items: ItemState[] = []
  tomes: ItemState[] = []

  enemies: Enemy[] = []
  projectiles: Projectile[] = []
  pickups: Pickup[] = []
  floaters: FloatingText[] = []
  particles: Particle[] = []
  blasts: Blast[] = [] // CF2: brief explosion footprint rings (render-only)

  time = 0 // seconds since run start
  phase: RunPhase = 'playing'
  gold = 0
  goldEarned = 0 // GROSS gold earned this run (never decremented by spending) — feeds meta.totalGoldEarned / "earn N gold" unlocks
  enemySpeedMul = 1 // global enemy move-speed multiplier (e.g. the Speed Demon challenge sets it >1)
  kills = 0
  bossesKilled = 0
  pendingLevelUps = 0
  revivesUsed = 0

  // level-up draft economy (reroll / banish / lock)
  banished = new Set<string>() // content ids removed from this run's draft pool
  disabledItems = new Set<string>() // item ids the player permanently toggled off (meta)
  banishCharges = 3
  lockedKey: string | null = null // "kind:id" of a card pinned to the next draft

  map: MapConfig
  // shared terrain heightfield params (same source the renderer samples) — set from the map
  terrainSeed = 0
  terrainAmp = 0
  terrainCfg: TerrainConfig = resolveTerrain()
  characterId: string
  passive = '' // active character passive id, read by combat hot paths
  gimmick = '' // active character signature gimmick (see updateGimmick / killEnemy)
  gimmickCd = 0 // cooldown timer for periodic gimmicks (aura/orbit/volley)
  // 'dice' hero (Roll): each level-up rolls ONE random permanent stat surge into this accumulator (deterministic via
  // world.rng), applied additively in recomputeStats. A whole run of rolls builds a wildly different character each time.
  diceStats = { damage: 0, attackSpeed: 0, critChance: 0, critMult: 0, maxHp: 0, armor: 0 }
  difficulty: number // tier multiplier (1 = base) — scales enemy hp + spawn density
  rewardMult = 1 // scales xp + gold drops (set from the chosen difficulty)

  // spawn director state
  spawnBudget = 0
  spawnAccum = 0
  nextBossAt: number
  waveTimer = 0
  eliteTimer = 0
  miniBossTimer = 45 // seconds until the next mini-boss beat
  affixSeq = 0 // deterministic elite/mini-boss affix selector — sim-only counter (NOT entity id), so the chosen affix is reproducible per seed regardless of render framerate
  // escalation rhythm: idle → calm (brief lull, telegraphs the surge) → surge (rate spike)
  surgePhase: 'idle' | 'calm' | 'surge' = 'idle'
  surgeT = 0 // time left in the current surge phase
  nextSurgeAt = 35 // run time of the next surge cycle
  // directional tides: a slowly drifting spawn-angle front so pressure comes from a shifting side
  tideAngle = 0 // current bias direction (radians)
  tideDrift = 0.18 // angular drift speed (rad/s), occasionally re-rolled
  tideTimer = 0 // countdown to the next drift re-roll
  vendorTimer = 50 // seconds until the next roaming vendor appears
  shrines: Shrine[] = []
  shrineTimer = 30 // seconds until the next shrine appears
  telegraphs: Telegraph[] = []
  objective: StageObjective = 'survive'
  stageGoal: StageGoal = 'survive' // MP6: what opens the portal this stage (survive timer / elite hunt / …)
  goalTarget = 0 // e.g. number of elites to kill for an elite-hunt
  goalProgress = 0 // progress toward goalTarget (e.g. elites killed)
  finalBossId = -1
  portalWarned = false // one-shot: emitted the "portal destabilizing" warning before the anti-stall auto-summon
  portal: Portal | null = null // boss teleporter (explore → reach → channel → boss)
  portalAt = 45 // seconds until the portal appears (set from map in constructor)
  finalSwarm = false // overtime climax: spawn pressure spikes once the boss is summoned
  finalSwarmT = 0 // seconds since the Final Swarm began — ramps the overtime spawn multiplier (rising climax, not flat)
  tier = 1 // stage tier 1-3 (higher = harder, chains more stages, better loot)
  stage = 0 // 0-based index of the current stage within the tier gauntlet
  stages = 1 // total stages this run (= tier): T1=1, T2=2, T3=3
  hitStop = 0 // brief global time-freeze for impact feel (game scales dt by this)
  lastHitStopAt = -999 // sim-time of the last hit-stop trigger — refractory so a dense kill/crit stream can't re-arm hitStop every frame (= permanent slow-mo). Sim-write-only; no sim decision reads it (canary-neutral).
  props: Prop[] = []
  obstacles: Obstacle[] = []
  corpses: Corpse[] = []
  cleared = false // beat the stage boss (records a win even if you continue endless)
  endless = false // endless mode: keep playing past the win with escalating difficulty
  breakables: Breakable[] = [] // destructible loot objects on the map
  curse = 0 // opt-in difficulty: more aliens + more frequent bosses, but more loot/luck
  challenge = '' // active challenge modifier id (read by recompute/spawns)
  bossRateMul = 1 // <1 = more frequent bosses (challenges)
  noHeal = false // Iron Man challenge: healing does nothing
  evoDamageMul = 1 // transient: scales evolved-weapon damage by their level (set per fire)

  // aggregate stat tracking for the run summary
  damageDealt = 0
  highestHit = 0
  xpCollected = 0

  // kill-combo economy (rising crunch as you chain kills)
  combo = 0
  comboTimer = 0 // seconds left before the combo resets
  bestCombo = 0

  // proc / trigger layer (populated from owned items in recomputeStats)
  procs: { hook: 'hit' | 'kill' | 'crit'; kind: string; level: number }[] = []
  frostReactions = false // MP4: owning Frozen Heart enables shatter/execute on frozen enemies
  combustReactions = false // PT1: owning Ember Core makes burning enemies burst a fire nova on death
  contagionReactions = false // PT1: owning Fungal Spores makes poisoned enemies spread spores on death
  conductReactions = false // PT1b: owning Capacitor makes weapon crits arc bonus lightning to a nearby foe
  marksman = false // active hero has the Marksman passive: weapons fire where you AIM (cone-targeting toward facing) + mouse-aim facing
  harvestKills = 0 // counter for every-Nth-kill procs (Soul Harvest)
  critMassStacks = 0 // Critical Mass: stacking damage from recent crits
  critMassTimer = 0 // seconds left before crit-mass stacks decay
  dmgSurgeT = 0 // seconds left of the Charge-shrine damage surge (timed +50% damage buff). 0 in the canary unless one is fully charged.
  inExplosions = false // guard: suppress procs while resolving AoE blasts (no cascades)
  weaponsLocked = 0 // seconds: boss weapon-lock power disables all but the starting weapon

  // per-frame feedback counters (reset each stepWorld; read by the audio/juice layer)
  shotsThisFrame = 0
  critsThisFrame = 0
  killsThisFrame = 0
  pickupsThisFrame = 0
  recoilThisFrame = false // a heavy weapon fired this frame (game adds a recoil kick)
  lastBombAt = -999 // run-time of the last screen-clear nuke drop (rate-limit)

  // transient flags consumed by the game layer
  events: string[] = []

  // queued AoE blasts (e.g. bomber death) resolved by the systems layer so the
  // combat module never has to import the player-damage path (avoids cycles)
  pendingExplosions: { x: number; z: number; radius: number; damage: number; color: number; weapon?: string }[] = []

  // end-of-run recap telemetry
  damageByWeapon: Record<string, number> = {} // total damage dealt, keyed by ownerWeapon ('effects' bucket for procs/gimmicks)
  killedBy = '' // enemy/boss defId that landed the killing blow on the player

  constructor(opts: {
    seed: number
    map: MapConfig
    characterId: string
    difficulty: number
    rewardMult?: number
  }) {
    this.seed = opts.seed >>> 0 // MP8: remember the run seed so it can be shown + shared/replayed
    this.rng = new RNG(opts.seed)
    this.map = opts.map
    // sim samples the same heightfield as the renderer (mapSeed(id) + map.elevation)
    this.terrainSeed = mapSeed(opts.map.id)
    this.terrainAmp = opts.map.elevation ?? 0
    this.terrainCfg = resolveTerrain(opts.map.terrain)
    this.characterId = opts.characterId
    this.difficulty = opts.difficulty
    this.rewardMult = opts.rewardMult ?? 1
    this.portalAt = this.map.goalSeconds ?? Math.min(45, this.map.surviveSeconds * 0.2)
    this.stats = baseStats()
    this.player = {
      x: 0,
      z: 0,
      vx: 0,
      vz: 0,
      y: 0,
      vy: 0,
      facing: 0,
      moveDir: { x: 0, z: 1 },
      radius: 0.55,
      hp: this.stats.maxHp,
      level: 1,
      xp: 0,
      xpToNext: xpForLevel(1),
      invuln: 0,
      dashCd: 0,
      dashTime: 0,
      jumpCd: 0,
      jumpBuf: 0,
      slideBuf: 0,
      alive: true,
      flash: 0,
      shield: 0,
      regenAccum: 0,
    }
    this.nextBossAt = 120 // first boss at 2:00
  }

  emit(ev: string): void {
    this.events.push(ev)
  }

  spawnEnemy(e: Enemy): Enemy {
    this.enemies.push(e)
    return e
  }

  spawnProjectile(p: Projectile): Projectile {
    this.projectiles.push(p)
    return p
  }

  spawnPickup(kind: PickupKind, x: number, z: number, value: number, rarity?: Rarity): Pickup {
    const tier = kind === 'xp' ? xpGemTier(value) : undefined
    // CF6: grab radius matches the drawn body — chest 0.8 (drawn 1.5), xp gems scale with their tier sprite
    const radius = kind === 'chest' ? 0.8 : kind === 'xp' ? gemRadius(tier ?? 1) : 0.35
    const p: Pickup = {
      id: allocId(),
      kind,
      alive: true,
      x,
      z,
      y: 0.3,
      vy: 3 + this.rng.next() * 2,
      value,
      radius,
      magnetized: false,
      bob: this.rng.next() * Math.PI * 2,
      rarity,
      tier,
    }
    this.pickups.push(p)
    return p
  }

  floatText(x: number, z: number, text: string, color: number, crit = false): void {
    // cap floaters so dense swarms don't flood the DOM overlay (perf)
    if (this.floaters.length >= 48) {
      if (!crit) return // drop non-crit numbers when saturated; keep crits visible
      this.floaters.shift()
    }
    this.floaters.push({
      id: allocId(),
      x,
      z,
      y: 1.4,
      text,
      color,
      life: crit ? 1.1 : 0.8,
      maxLife: crit ? 1.1 : 0.8,
      vy: crit ? 2.4 : 1.8,
      crit,
    })
  }

  burst(x: number, z: number, color: number, count: number, power = 1): void {
    for (let i = 0; i < count; i++) {
      const a = this.rng.next() * Math.PI * 2
      const s = (1.5 + this.rng.next() * 3) * power
      this.particles.push({
        id: allocId(),
        x,
        z,
        y: 0.6,
        vx: Math.cos(a) * s,
        vz: Math.sin(a) * s,
        vy: 2 + this.rng.next() * 3 * power,
        life: 0.4 + this.rng.next() * 0.4,
        maxLife: 0.8,
        color,
        size: 0.12 + this.rng.next() * 0.18,
      })
    }
  }

  /** CF2 — queue a brief footprint ring at an explosion's actual lethal radius (render-only, no rng). */
  blast(x: number, z: number, radius: number, color: number): void {
    this.blasts.push({ x, z, radius, t: 0, maxT: 0.45, color })
  }

  hasWeapon(id: string): WeaponState | undefined {
    return this.weapons.find((w) => w.defId === id)
  }

  /** Credit gold AND the gross run-earnings counter (spending only touches `gold`, so this is the true "earned"). */
  gainGold(n: number): void {
    this.gold += n
    this.goldEarned += n
  }

  hasItem(id: string): ItemState | undefined {
    return this.items.find((i) => i.defId === id)
  }

  hasTome(id: string): ItemState | undefined {
    return this.tomes.find((tm) => tm.defId === id)
  }
}

/** XP required to go from `level` to `level+1`. Fast early cadence, escalating late. */
export function xpForLevel(level: number): number {
  return Math.floor(4 + level * 2 + level * level * 0.9)
}
